import { supabase } from "./supabase";
import campaignServiceEnhanced from "./campaignServiceEnhanced";

const APOLLO_API_KEY = import.meta.env.VITE_APOLLO_API_KEY;

const callApolloProxy = async (
  endpoint,
  method = "GET",
  body = null,
  queryParams = null,
  oauthToken = null
) => {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("No active session. Please log in.");

    let url = `/.netlify/functions${endpoint}`;
    if (queryParams) {
      const searchParams = new URLSearchParams(queryParams);
      url += `?${searchParams.toString()}`;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    };

    if (oauthToken) {
      headers["Authorization"] = `Bearer ${oauthToken}`;
    } else if (APOLLO_API_KEY) {
      headers["Api-Key"] = APOLLO_API_KEY;
    } else {
      throw new Error("No API key or OAuth token provided");
    }

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });

    const text = await response.text();
    try {
      const data = JSON.parse(text);
      console.log("Apollo API Response:", data);
      if (!data.success)
        throw new Error(data.error || `HTTP ${data.status}: Request failed`);
      return data.data;
    } catch (jsonError) {
      console.error("JSON Parse Error:", text);
      throw new Error(`Invalid JSON response: ${text.slice(0, 100)}`);
    }
  } catch (error) {
    console.error("Apollo Service Error:", error);
    throw new Error(
      error.message.includes("fetch") || error.message.includes("NetworkError")
        ? "Cannot connect to Apollo API. Check your internet connection."
        : error.message
    );
  }
};

const apolloService = {
  searchLeads: async (filters, oauthToken = null) => {
    try {
      const queryParams = {
        q_organization_job_titles: filters.jobTitles?.join(",") || "",
        q_organization_industries: filters.industries?.join(",") || "",
        q_person_locations: filters.locations?.join(",") || "",
        q_organization_sizes: filters.companySizes?.join(",") || "",
        per_page: filters.limit || 5,
      };

      const data = await callApolloProxy(
        "/v1/people/search",
        "GET",
        null,
        queryParams,
        oauthToken
      );
      const leads =
        data.people?.map((lead, index) => ({
          full_name:
            lead.name ||
            `${lead.first_name || ""} ${lead.last_name || ""}`.trim() ||
            `Lead ${index + 1}`,
          first_name: lead.first_name || "Unknown",
          last_name: lead.last_name || "Unknown",
          job_title: lead.title || "Unknown Position",
          company: lead.organization?.name || "Unknown Company",
          location:
            lead.city || lead.state || lead.country || "Unknown Location",
          linkedin_url: lead.linkedin_url || "",
          email: lead.email || null,
          phone: lead.phone || null,
          profile_image_url: lead.photo_url || null,
          apollo_lead_id: lead.id || `temp_${Date.now()}_${index}`,
          lead_data: {
            industry: lead.organization?.industry || null,
            companySize: lead.organization?.estimated_size || null,
            department: lead.department || null,
            seniority: lead.seniority || null,
          },
        })) || [];

      return {
        success: true,
        data: {
          leads,
          total: data.pagination?.total_entries || leads.length,
          hasMore: data.pagination?.next_page || false,
        },
      };
    } catch (error) {
      console.error("Search Leads Error:", error.message);
      return {
        success: false,
        error: error.message || "Lead search failed",
      };
    }
  },

  startLeadFetching: async (
    apolloCampaignId,
    targetingCriteria,
    campaignId,
    oauthToken = null
  ) => {
    try {
      const data = await callApolloProxy(
        `/v1/people/bulk_search/${apolloCampaignId}`,
        "POST",
        {
          q_organization_job_titles:
            targetingCriteria.jobTitles?.join(",") || "",
          q_organization_industries:
            targetingCriteria.industries?.join(",") || "",
          q_person_locations: targetingCriteria.locations?.join(",") || "",
          per_page: targetingCriteria.max_leads || 100,
        },
        null,
        oauthToken
      );

      if (campaignId) {
        await campaignServiceEnhanced.updateCampaignStatus(
          campaignId,
          "completed"
        );
      }

      return {
        success: true,
        data: {
          fetchingStarted: true,
          estimatedLeads: data.pagination?.total_entries || 0,
          message: data.message || "Lead fetching started successfully",
        },
      };
    } catch (error) {
      console.error("Start Lead Fetching Error:", error.message);
      return {
        success: false,
        error: error.message || "Failed to start lead fetching",
      };
    }
  },

  validateConnection: async (oauthToken = null) => {
    try {
      const data = await callApolloProxy(
        "/v1/auth/validate",
        "GET",
        null,
        null,
        oauthToken
      );
      return {
        success: true,
        data: {
          valid: data.valid,
          account: data.account_info,
        },
      };
    } catch (error) {
      console.error("Validate Connection Error:", error.message);
      return {
        success: false,
        error: error.message || "Validation failed",
      };
    }
  },

  getAuthorizationUrl: async (userId) => {
    try {
      const baseUrl = window.location.origin;
      const redirectUri = `${baseUrl}/auth/apollo/callback`;

      // Call backend API route to get OAuth URL from Apollo (prefers /api/apollo/url)
      const response = await fetch("/api/apollo/url", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          redirect_uri: redirectUri,
          user_id: userId,
        }),
      });

      // Read raw response text first (safer when endpoint may return HTML)
      const text = await response.text();
      console.log("/.netlify/functions/apollo-auth-url raw response:", text);

      // Try to parse JSON, but handle HTML/invalid JSON gracefully
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse JSON from apollo-auth-url:", text);
        return {
          success: false,
          error: "Invalid JSON response from auth-url endpoint",
          rawText: text,
        };
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to get authorization URL");
      }

      return {
        success: true,
        data: {
          authUrl: data.authUrl,
          state: data.state,
        },
      };
    } catch (error) {
      console.error("Get Authorization URL Error:", error.message);
      return {
        success: false,
        error: error.message || "Failed to get authorization URL",
      };
    }
  },

  /**
   * Handle Apollo OAuth Callback
   * Exchange authorization code for access token
   */
  handleAuthCallback: async (code, state, userId) => {
    try {
      // Prefer server-side API route for token exchange
      const response = await fetch("/api/apollo/callback", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          state,
          user_id: userId,
        }),
      });

      // Read raw response text first and log it for debugging
      const text = await response.text();
      console.log(
        "/.netlify/functions/apollo-auth-callback raw response:",
        text
      );

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse JSON from apollo-auth-callback:", text);
        return {
          success: false,
          error: "Invalid JSON response from auth-callback endpoint",
          rawText: text,
        };
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to complete authorization");
      }

      // Save OAuth token to Supabase user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          apollo_oauth_token: data.accessToken,
          apollo_token_expires_at: data.expiresAt,
        },
      });

      if (updateError) {
        console.error("Error saving OAuth token:", updateError);
        throw updateError;
      }

      return {
        success: true,
        data: {
          accessToken: data.accessToken,
          message: "Apollo account connected successfully!",
        },
      };
    } catch (error) {
      console.error("Auth Callback Error:", error.message);
      return {
        success: false,
        error: error.message || "Failed to authorize Apollo account",
      };
    }
  },

  /**
   * Get stored OAuth token for user
   */
  getOAuthToken: async (userId) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return {
          success: false,
          error: "No active user session",
        };
      }

      const oauthToken = user.user_metadata?.apollo_oauth_token;
      const expiresAt = user.user_metadata?.apollo_token_expires_at;

      if (!oauthToken) {
        return {
          success: false,
          error: "Apollo account not connected",
        };
      }

      // Check if token is expired
      if (expiresAt && new Date(expiresAt) < new Date()) {
        return {
          success: false,
          error: "Apollo token expired. Please reconnect your account.",
          needsRefresh: true,
        };
      }

      return {
        success: true,
        data: {
          token: oauthToken,
          expiresAt: expiresAt,
        },
      };
    } catch (error) {
      console.error("Get OAuth Token Error:", error.message);
      return {
        success: false,
        error: error.message || "Failed to retrieve OAuth token",
      };
    }
  },

  /**
   * Fetch and save leads to campaign
   * Automatically processes leads and saves them to apollo_leads table
   */
  fetchAndSaveLeads: async (
    campaignId,
    userId,
    targetingCriteria,
    oauthToken
  ) => {
    try {
      const response = await apolloService.searchLeads(
        targetingCriteria,
        oauthToken
      );

      if (!response.success) {
        throw new Error(response.error);
      }

      const leads = response.data.leads || [];

      // Save leads to apollo_leads table
      const { data: savedLeads, error: insertError } = await supabase
        .from("apollo_leads")
        .insert(
          leads.map((lead) => ({
            campaign_id: campaignId,
            user_id: userId,
            full_name: lead.full_name,
            first_name: lead.first_name,
            last_name: lead.last_name,
            job_title: lead.job_title,
            company: lead.company,
            location: lead.location,
            email: lead.email,
            phone: lead.phone,
            linkedin_url: lead.linkedin_url,
            profile_image_url: lead.profile_image_url,
            apollo_lead_id: lead.apollo_lead_id,
            metadata: lead.lead_data || {},
            engagement_status: "not_contacted",
            created_at: new Date().toISOString(),
          }))
        )
        .select();

      if (insertError) {
        console.error("Error saving leads:", insertError);
        throw insertError;
      }

      return {
        success: true,
        data: {
          leadsCreated: savedLeads?.length || 0,
          leads: savedLeads || [],
          message: `Successfully imported ${savedLeads?.length || 0} leads`,
        },
      };
    } catch (error) {
      console.error("Fetch and Save Leads Error:", error.message);
      return {
        success: false,
        error: error.message || "Failed to fetch and save leads",
      };
    }
  },
};

export default apolloService;
